import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/20/solid';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import {
	Alert,
	Badge,
	Button,
	Chip,
	Input,
	ThemeProvider,
	Typography,
} from '@material-tailwind/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Datepicker from "react-tailwindcss-datepicker";
import { OrderSearchParams, OrderSummary } from '../../types';
import { getDateInputValue } from '../lib/date';
import { formatDateTimeLong, formatDateTimeShort, getStatusColor } from '../lib/formatters';
import useChatrooms from '../store/chatrooms';
import { useActiveOrderFilters, useOrderHistoryFilters } from '../store/filters';
import ChipSelector from './ChipSelector';
import OrderStatusChip from './OrderStatusChip';
import { Pagination } from './Pagination';

interface Query {
	query: string;
	fieldNamesToSearch: string[];
	regex: RegExp;
}

interface PaginationProps {
	activePage: number;
	setActivePage: (activePage: number) => void;
	pageSize: number;
    setPageSize: (pageSize: number) => void;
}
interface Props {
	type: string;
	orders: Record<string, OrderSummary> | OrderSummary[];
	ordersLoaded: boolean;
	setOrderSearchParams?: (value: OrderSearchParams) => void;
	orderDateQuery?: string;
	setOrderDateQuery?: (value: string) => void;
	searchButtonDisabled?: boolean;
	setSearchButtonDisabled?: (value: boolean) => void;
	noHistorySearchCriteria?: boolean;
	paginationProps?: PaginationProps | null;
}

const statusSortPriority = {
	'Return in progress': 0,
	'At consumer': 1,
	'Delivery in progress': 2,
	'Waiting for pickup': 3,
	Preparing: 4,
	Placed: 5,
	Delivered: 6,
	Returned: 7,
	Voided: 8,
};

const theme = {
	input: {
		styles: {
			base: {
				container: {
					minWidth: 'min-w-[50px]',
				},
			},
		},
	},
};

const OrderTable = ({
	type,
	orders = [],
	ordersLoaded,
	setOrderSearchParams = () => {},
	searchButtonDisabled = false,
	setSearchButtonDisabled = () => {},
	noHistorySearchCriteria = false,
	paginationProps = null,
}: Props) => {
	const filterState = type === 'history' ? useOrderHistoryFilters : useActiveOrderFilters;

	const {
		sortField,
		setSortField,
		sortDirection,
		setSortDirection,
		dateBounds,
		setDateBounds,
		generalSearchQuery,
		setGeneralSearchQuery,
		consumerNameQuery,
		setConsumerNameQuery,
		orderIdQuery,
		setOrderIdQuery,
		producerNameQuery,
		setProducerNameQuery,
		orderSourceQuery,
		setOrderSourceQuery,
		orderStatusQuery,
		setOrderStatusQuery,
		orderTabSelect,
		orderDateQuery,
		setOrderDateQuery,
	} = filterState();

	const debouncedSetConsumerNameQuery = _.debounce(setConsumerNameQuery, 500);
	const debouncedSetOrderIdQuery = _.debounce(setOrderIdQuery, 500);
	const debouncedSetProducerNameQuery = _.debounce(setProducerNameQuery, 500);
	const debouncedSetOrderSourceQuery = _.debounce(setOrderSourceQuery, 500);
	const debouncedSetOrderStatusQuery = _.debounce(setOrderStatusQuery, 500);

	const {chatrooms} = useChatrooms();

	const DEFAULT_DATE_BOUNDS = {
		min: getDateInputValue(30), // 30 days ago
		max: getDateInputValue(0), // today
	}

	const statusOptions = (type === 'active' ? [
		{value: '', label: 'Any', variant: 'ghost'},
		{value: 'placed', label: 'Placed'},
		{value: 'preparing', label: 'Preparing'},
		{value: 'waiting for pickup', label: 'Waiting for pickup'},
		{value: 'delivery in progress', label: 'Delivery in progress'},
		{value: 'at consumer', label: 'At consumer'},
		{value: 'delivered', label: 'Delivered'},
		{value: 'voided', label: 'Voided'},
		{value: 'return in progress', label: 'Return in progress'},
		{value: 'returned', label: 'Returned'},
		{value: 'partially returned', label: 'Partially returned'}
	] : [
		{value: '', label: 'Any', variant: 'ghost'},
		{value: 'delivered', label: 'Delivered'},
		{value: 'voided', label: 'Voided'},
		{value: 'returned', label: 'Returned'},
		{value: 'in_progress', label: 'In Progress'}
	]).map((option) => ({...option, color: getStatusColor(option.label)}));

	const headers = [
		{
			label: 'Order Id',
			fieldName: 'externalId',
			rowValueName: 'externalId',
			inputType: 'text',
			initialValue: orderIdQuery,
			setter: debouncedSetOrderIdQuery,
		},
		type === 'active'
			? {
					label: 'Placed',
					fieldName: 'time.placed',
					rowValueName: 'date',
					inputType: 'date',
					initialValue: orderDateQuery,
					setter: setOrderDateQuery,
			  }
			: {
					label: 'Completed',
					fieldName: 'time.lateDelivery',
					rowValueName: 'date',
					inputType: 'date',
					initialValue: orderDateQuery,
					setter: setOrderDateQuery,
			  },
		{
			label: 'Source',
			fieldName: 'source.label',
			rowValueName: 'orderSourceLabel',
			inputType: 'text',
			initialValue: orderSourceQuery,
			setter: debouncedSetOrderSourceQuery,
		},
		{
			label: 'Producer',
			fieldName: 'producer.name',
			rowValueName: 'producerName',
			inputType: 'text',
			initialValue: producerNameQuery,
			setter: debouncedSetProducerNameQuery,
		},
		{
			label: 'Consumer',
			fieldName: 'consumer.name',
			rowValueName: 'consumerName',
			initialValue: consumerNameQuery,
			setter: debouncedSetConsumerNameQuery,
		},
		{
			label: 'Status',
			fieldName: 'status.order',
			rowValueName: 'orderStatus',
			inputType: 'text',
			initialValue: orderStatusQuery,
			setter: debouncedSetOrderStatusQuery,
			disableSort: type === 'active',
			customComponent: generalSearchQuery && generalSearchQuery !== '' ? null : (setter) => ChipSelector({ setter, label: 'Status', options: statusOptions, value: orderStatusQuery }),
		},
	];

	const allQueries: Query[] = [
		{
			query: consumerNameQuery,
			fieldNamesToSearch: ['consumer.name'],
			regex: /.*/,
		},
		{
			query: orderIdQuery,
			fieldNamesToSearch: ['externalId', 'orderKey'],
			regex: /.*/,
		},
		{
			query: producerNameQuery,
			fieldNamesToSearch: ['producer.name'],
			regex: /.*/,
		},
		{
			query: orderSourceQuery,
			fieldNamesToSearch: ['source.label'],
			regex: /.*/,
		},
		{
			query: orderDateQuery,
			fieldNamesToSearch: [
				`time.${type === 'active' ? 'placed' : 'lateDelivery'}`,
			],
			regex: /.*/,
		},
		{
			query: orderStatusQuery,
			fieldNamesToSearch: ['status.order'],
			regex: /.*/,
		},
	];

	const sortByStatusAndTime = (
		orders: OrderSummary[],
		sortDirection: number
	): OrderSummary[] => {
		return Object.values(orders).sort((a, b) => {
			const aStatus = a.status.order;
			const bStatus = b.status.order;
			let defaultSort =
				sortDirection *
				(statusSortPriority[aStatus] - statusSortPriority[bStatus]);
			if (defaultSort === 0) {
				defaultSort =
					-1 *
					(new Date(a.updatedAt).getTime() -
						new Date(b.updatedAt).getTime()); // always descending by time within same status
			}
			return defaultSort;
		});
	};

	const sortByField = (
		orders: OrderSummary[],
		fieldName: string,
		sortDirection: number
	): OrderSummary[] => {
		const sortedOrders = Object.values(orders).sort((a, b) => {
			let defaultSort = 0;
			let aValue: string = _.get(a, fieldName);
			let bValue: string = _.get(b, fieldName);

			if (typeof aValue === 'string') {
				aValue = aValue.toLowerCase();
				bValue = bValue.toLowerCase();
			}

			if (aValue < bValue) {
				defaultSort = -1;
			}

			if (aValue > bValue) {
				defaultSort = 1;
			}

			return sortDirection * defaultSort;
		});

		return sortedOrders;
	};

	const generateRowSortFunction = (
		fieldName: string,
		sortDirection: number
	) => {
		if (fieldName === 'status.order') {
			return (orders: OrderSummary[]) =>
				sortByStatusAndTime(orders, sortDirection);
		}

		return (orders: OrderSummary[]) =>
			sortByField(orders, fieldName, sortDirection);
	};

	const generateColumnFilterFunction = (queries: Query[]) => {
		queries.forEach((queryObj) => {
			const { query } = queryObj;

			if (query !== '') {
				queryObj.regex = new RegExp(query, 'i');
			} else {
				queryObj.regex = /.*/;
			}
		});

		return (orders: OrderSummary[]) => {
			return _.filter(orders, (order: OrderSummary) => {
				return queries.every((queryObj) => {
					const { fieldNamesToSearch, regex } = queryObj;
					return fieldNamesToSearch.some((fieldName) => {
						const value: string = _.get(order, fieldName);
						return regex.test(value);
					});
				});
			});
		};
	};

	const generateTabSelectFilterFunction = (tabValue: string) => {
		return (orders: OrderSummary[]) => {
			if (tabValue === 'pending') {
				return _.filter(orders, (order: OrderSummary) => {
					return [
						'At consumer',
						'Waiting for pickup',
						'Preparing',
						'Delivery in progress',
						'Placed',
					].includes(order.status.order);
				});
			} else if (tabValue === 'unread') { 
				return _.filter(orders, (order: OrderSummary) => {
					const {orderKey} = order;
					const chatroomHasNewMessage = chatrooms[orderKey] && !chatrooms[orderKey].hasBeenReadByParticipant;
					return chatroomHasNewMessage;
				});
			} else {
				// all
				return orders;
			}
		};
	};

	const changeSortDirection = ({ mode }) => {
		if (mode === 'toggle') {
			setSortDirection(sortDirection * -1);
		}
		if (mode === 'reset') {
			setSortDirection(1);
		}
	};

	const rows: any = useMemo(() => {
		const orderSortAndFilterFunctions =
			type === 'active'
				? [
						generateTabSelectFilterFunction(orderTabSelect),
						generateRowSortFunction(sortField, sortDirection),
						generateColumnFilterFunction(allQueries),
				  ]
				: [];

		let sortedAndFilteredOrders: OrderSummary[] = _.values(orders);
		orderSortAndFilterFunctions.forEach((fn) => {
			sortedAndFilteredOrders = fn(sortedAndFilteredOrders);
		});

		return sortedAndFilteredOrders.map((order: OrderSummary) => {
			const numberOfCompletedDeliveries = _.filter(
				order.status?.deliveries ?? [],
				(d) => d === 'Delivered'
			).length;
			const totalNumberOfDeliveries = (order.deliveries ?? []).length;
			const rowOrder = {};
			headers.forEach((header) => {
				rowOrder[header.rowValueName] = _.get(order, header.fieldName);
			});
			return {
				...rowOrder,
				orderKey: order.orderKey,
				numberOfCompletedDeliveries,
				totalNumberOfDeliveries,
				isFutureOrder: order.time?.isFutureOrder ?? false,
				lateDelivery: order.time?.lateDelivery,
				updatedAt: order.updatedAt,
				isRedelivery: order.isRedelivery,
			};
		});
	}, [
		orders,
		orderTabSelect,
		sortField,
		sortDirection,
		consumerNameQuery,
		orderIdQuery,
		producerNameQuery,
		orderSourceQuery,
		orderDateQuery,
		orderStatusQuery,
	]);

	useEffect(() => {
		if ((dateBounds && dateBounds.startDate && dateBounds.endDate) || (generalSearchQuery && generalSearchQuery !== '')) {
			setOrderSearchParams({
				dateRange: dateBounds,
				generalSearchQuery,
				sortField,
				sortDirection,
				orderStatus: orderStatusQuery === '' ? null : orderStatusQuery,
			});
		}
	}, [sortField, sortDirection, orderStatusQuery]);


	useEffect(() => {
		if (generalSearchQuery === '' && (!dateBounds?.startDate || !dateBounds?.endDate)) {
			setSearchButtonDisabled(true);
		} else {
			setSearchButtonDisabled(false);
		}

		if (dateBounds && !dateBounds.startDate && !dateBounds.endDate) { // clears the table when date is cleared
			setOrderSearchParams({
				dateRange: dateBounds,
				generalSearchQuery
			});
		}

	}, [dateBounds, generalSearchQuery, orderStatusQuery])

	const disableGeneralSearch = window.location.hostname === 'dev-super-admin.relay.delivery';
	
	return (
		<ThemeProvider value={theme}>
			<div className="flex justify-between items-center p-3 h-20">
				<h5 className="text-xl font-semibold">
					{type === 'active' ? 'Active Orders' : 'Order History'}
				</h5>
				{type === 'history' && (
					<div className="w-[70%] px-6 flex items-center justify-between gap-4">
						<div className="w-[50%] flex items-center justify-between gap-4">
							<Datepicker
								value={dateBounds}
								asSingle={true}
								onChange={(newDateBounds) => setDateBounds(newDateBounds)} 
								placeholder='Enter desired date range'
								minDate={DEFAULT_DATE_BOUNDS.min}
								maxDate={DEFAULT_DATE_BOUNDS.max}
								useRange={false}
							/> 
						</div>
						<Input
							label={disableGeneralSearch? "Not available in dev environment.": "General Search (name, phone numbers, emails, etc.)"}
							crossOrigin=""
							variant="standard"
							size="md"
							type={'text'}
							disabled={disableGeneralSearch}
							value={generalSearchQuery}
							onChange={(event) => {
								setGeneralSearchQuery(
									event.target.value
								);
							}}
						/>
						<Button 
							className='w-[20%]'
							onClick={() => {
								setOrderSearchParams({
									dateRange: dateBounds,
									generalSearchQuery,
									sortField,
									sortDirection,
									orderStatus: orderStatusQuery === '' ? null : orderStatusQuery,
								});
								setSearchButtonDisabled(true);
							}} 
							disabled={searchButtonDisabled}
						>
							Search!
						</Button>
					</div>
				)}
			</div>
			<div className="flex-col overflow-visible max-h-[calc(100vh-10rem)] items-center">
				<table className="border mt-4 w-full table-auto text-left overflow-auto">
					<TableHeaders
						type={type}
						headers={headers}
						sortField={sortField}
						setSortField={setSortField}
						changeSortDirection={changeSortDirection}
						generalSearchQuery={generalSearchQuery}
					/>
					<TableRows 
						rows={rows}
						ordersLoaded={ordersLoaded}
						headers={headers}
						noHistorySearchCriteria={noHistorySearchCriteria}
						type={type}
						chatrooms={chatrooms}
					/>
				</table>
				{paginationProps && (
					<Pagination
						activePage={paginationProps.activePage}
						setActivePage={paginationProps.setActivePage}
						pageSize={paginationProps.pageSize} setPageSize={paginationProps.setPageSize}
					/>
				)}
			</div>
		</ThemeProvider>
	);
};

const TableHeaders = ({
	type,
	headers,
	sortField,
	setSortField,
	changeSortDirection,
	generalSearchQuery,
}) => {
	const yesterday = new Date(new Date().setHours(-1));
	const yesterdayString = DateTime.fromISO(yesterday.toISOString()).toFormat(
		'yyyy-MM-dd'
	);

	const [headerValues, setHeaderValues] = useState(headers.map(h => h.initialValue))

	return (
		<thead>
			<tr>
				{headers.map((head, index) => (
					<th
						key={head.label}
						className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50"
					>
						<div className="flex items-center justify-between gap-2 font-normal leading-none">
							<div className="w-full gap-6">
								{type === 'history' && !head.customComponent ? 
									<div>{head.label}</div>
									: !head.customComponent ? <Input
										label={head.label}
										crossOrigin=""
										variant="standard"
										size="md"
										value={headerValues[index]}
										placeholder={head.placeholder}
										type={head.inputType}
										onChange={(event) => {
											setHeaderValues({
												...headerValues,
												[index]: event.target.value
											})
											head.setter(event.target.value)
										}}
									/> : head.customComponent(head.setter)
								}
							</div>
							{// TEMP: disable sort fields other than completed date while opensearch reindexes with keywords
								(
									(type !== 'history' || (!generalSearchQuery || generalSearchQuery === '') || head.fieldName === 'time.lateDelivery') &&
									!head.disableSort
								) && 
								<ChevronUpDownIcon
									strokeWidth={2}
									className="h-4 w-4"
									onClick={() => {
										setSortField(head.fieldName);
										const mode =
											head.fieldName === sortField
												? 'toggle'
												: 'reset';
										changeSortDirection({ mode });
									}}
								/>
							}
						</div>
					</th>
				))}
			</tr>
		</thead>
	);
};

const TableRows = ({
	rows,
	ordersLoaded,
	headers,
	noHistorySearchCriteria,
	type,
	chatrooms,
}) => {
	const navigate = useNavigate();

	return (
		<tbody>
			{rows.length === 0 && ordersLoaded && (
				<tr className="border-b-0">
					<td colSpan={headers.length} className="p-8 py-4">
						<Alert
							color={noHistorySearchCriteria ? 'blue' : 'orange'}
							variant="ghost"
						>
							{noHistorySearchCriteria
								? 'Please enter a date or general search query'
								: 'No Orders Found'}
						</Alert>
					</td>
				</tr>
			)}
			{!ordersLoaded && rows.length === 0 && (
				<tr className="border-b-0">
					<td colSpan={headers.length} className="p-8 py-4">
						<Alert color="orange" variant="ghost">
							Loading...
						</Alert>
					</td>
				</tr>
			)}
			{rows.map(
				(
					{
						externalId,
						orderKey,
						orderSourceLabel,
						producerName,
						consumerName,
						date,
						orderStatus,
						numberOfCompletedDeliveries,
						totalNumberOfDeliveries,
						isFutureOrder,
						lateDelivery,
						updatedAt,
						isRedelivery,
					},
					index
				) => {
					const isLast = index === rows.length - 1;
					const classes = isLast
						? 'p-4'
						: 'p-4 border-b border-blue-gray-50';

					const chatroomHasNewMessage = chatrooms[orderKey] && !chatrooms[orderKey].hasBeenReadByParticipant;

					return (
						<tr
							key={orderKey}
							className={ordersLoaded ? "cursor-pointer hover:bg-gray-100" : "pointer-events-none bg-gray-100"}
							onClick={() =>
									navigate(
									`/${
										type === 'active'
											? 'active-orders'
											: 'order-history'
									}/${orderKey}`
								)
							}
						>
							<td className={classes}>
								<div className="flex items-start">
									<Badge 
										content={<ChatBubbleLeftEllipsisIcon/>} 
										invisible={!chatroomHasNewMessage} 
										placement="top-end"
									>
									<div className={`flex flex-col gap-y-1 pr-5`}>
											<div className={`flex items-center`}>
												<Typography
													variant="small"
													color="blue-gray"
													className="font-semibold"
												>
													{externalId}
												</Typography>

												{isRedelivery && (
													<div className="flex items-center ml-2">
														<Chip
															variant="ghost"
															size="sm"
															value="Redelivery"
															color="amber"
														/>
													</div>
												)}
											</div>
										

										<Typography
											variant="small"
											color="blue-gray"
											className="font-normal opacity-70"
										>
											{orderKey}
										</Typography>
									</div>
									</Badge>
								</div>
							</td>
							<td className={classes}>
								<Typography
									variant="small"
									color="blue-gray"
									className="font-normal"
								>
									{formatDateTimeLong(date)}
								</Typography>
							</td>
							<td className={classes}>
								<div className="flex flex-col">
									<Typography
										variant="small"
										color="blue-gray"
										className="font-semibold"
									>
										{orderSourceLabel}
									</Typography>
								</div>
							</td>
							<td className={classes}>
								<div className="flex flex-col">
									<Typography
										variant="small"
										color="blue-gray"
										className="font-normal"
									>
										{producerName}
									</Typography>
								</div>
							</td>
							<td className={classes}>
								<div className="flex flex-col">
									<Typography
										variant="small"
										color="blue-gray"
										className="font-semibold"
									>
										{consumerName}
									</Typography>
								</div>
							</td>
							<td className={classes}>
								<div className='flex flex-col gap-1'>
								<div className="flex w-full justify-left items-center gap-x-2">
									<OrderStatusChip
										orderStatus={orderStatus}
										className={'justify-center h-max'}
									/>
									{totalNumberOfDeliveries > 1 && (
										<Chip
											variant="ghost"
											size="sm"
											className="justify-center w-max h-max"
											value={`${totalNumberOfDeliveries} Couriers`}
											color="teal"
										/>
									)}
									<Typography
										variant="small"
										color="blue-gray"
										className="font-normal italic w-full text-right opacity-70"
									>
										{`${formatDateTimeShort(updatedAt)}`}
									</Typography>
								</div>

								{isFutureOrder &&
									[
										'Placed',
										'Preparing',
										'Waiting for pickup',
									].includes(orderStatus) && (
										<Typography
											variant="small"
											color="blue-gray"
											className="font-normal italic"
										>
											{`Future order due on ${formatDateTimeShort(
												lateDelivery
											)}`}
										</Typography>
									)}
								</div>
							</td>
						</tr>
					);
				}
			)}
		</tbody>
	);
};

export default OrderTable;
